import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Testimonials from "@/components/Testimonials";
import { motion } from "framer-motion";
import VideoSlot from "@/components/VideoSlot";
import PackShowcase from "@/components/PackShowcase";
import FoundersHero from "@/components/FoundersHero";
import WatchVideoButton from "@/components/WatchVideoButton";
import {
  ShieldCheck, MessageSquareText, FileText, GraduationCap, Rocket,
  ArrowRight, Bell, Check, Sparkles, Building2, Stethoscope, Syringe, MousePointerClick } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

const FEATURES = [
  { icon: MessageSquareText, title: "AI Compliance Assistant", body: "Instant, UK-specific answers on CQC key questions and Ofsted standards — with a specialist on standby when it matters." },
  { icon: FileText, title: "Ready-to-use Policies", body: "Inspection-ready policy & procedure packs mapped to the Single Assessment Framework and Children's Homes Regs." },
  { icon: GraduationCap, title: "Mock Interviews", body: "Practise the registered manager / responsible individual interview with detailed, personalised feedback." },
  { icon: Bell, title: "Live Regulatory Updates", body: "Automatic alerts on changes to CQC and Ofsted regulations and guidance, so you're notified when something relevant changes." },
];

const HERO_LINKS = { adults: "/cqc", children: "/ofsted", clinical: "/aesthetics", beauty: "/start-up?track=aesthetics", hospitals: "/gp-hospitals", fostering: "/fostering" };
const HERO_IMAGES = [
  { id: "adults", src: "https://images.unsplash.com/photo-1702648156180-25d8be9c9527?crop=entropy&cs=srgb&fm=jpg&q=80&w=800", alt: "Carer supporting an older person in a residential care home", tag: "Adult social care" },
  { id: "children", src: "https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?crop=entropy&cs=srgb&fm=jpg&q=80&w=800", alt: "Group of teenagers laughing together", tag: "Children & young people" },
  { id: "clinical", src: "https://images.unsplash.com/photo-1512290923902-8a9f81dc236c?crop=entropy&cs=srgb&fm=jpg&q=80&w=800", alt: "Clinician giving a cosmetic injectable treatment in a clinic", tag: "Clinical aesthetics" },
  { id: "beauty", src: "https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?crop=entropy&cs=srgb&fm=jpg&q=80&w=800", alt: "Beauty therapist applying a facial treatment", tag: "Beauty & wellbeing" },
  { id: "hospitals", src: "https://images.unsplash.com/photo-1551190822-a9333d879b1f?crop=entropy&cs=srgb&fm=jpg&q=80&w=800", alt: "Surgical team in theatre at an independent hospital", tag: "GP & hospitals" },
  { id: "fostering", src: "https://images.unsplash.com/photo-1609220136736-443140cffec6?crop=entropy&cs=srgb&fm=jpg&q=80&w=800", alt: "Foster carer with two happy children outdoors", tag: "Fostering agencies" },
];

const TRACKS = [
  { icon: Stethoscope, tag: "Registered Managers", title: "Stay inspection-ready", img: HERO_IMAGES[0], points: ["AI assistant & specialist escalation", "Policy libraries & audit checklists", "Regulatory change alerts"], to: "/pricing", cta: "See plans" },
  { icon: Syringe, tag: "Clinical Aesthetics", title: "CQC-ready aesthetics clinics", img: HERO_IMAGES[2], points: ["Which treatments are regulated activities", "Prescribing, consent & complications policies", "Aesthetics fit-person mock interview"], to: "/start-up?track=aesthetics", cta: "Explore the aesthetics track" },
  { icon: Rocket, tag: "New Start-ups", title: "Register with confidence", img: HERO_IMAGES[3], points: ["Step-by-step registration roadmap", "Statement of Purpose & policy starter kit", "Mock registration interview"], to: "/start-up", cta: "Start your journey" },
];

export default function Landing() {
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="landing-hero">
        <div className="absolute inset-0 bg-grid-texture" aria-hidden="true" />
        <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-brandteal/20 blur-3xl" aria-hidden="true" />
        <div className="absolute -bottom-40 right-0 w-[560px] h-[560px] rounded-full bg-brandviolet/15 blur-3xl" aria-hidden="true" />
        <img src="/futuregen-logo.png" alt="" aria-hidden="true" className="hidden lg:block absolute -right-16 -top-10 w-[420px] opacity-[0.06] rotate-12 pointer-events-none select-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 lg:pt-14 lg:pb-24">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <motion.div initial="hidden" animate="show" variants={fadeUp} className="lg:col-span-7">
              <span className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-accent/30 bg-accent/10 text-accent text-xs sm:text-sm font-bold uppercase tracking-[0.14em] mb-6" data-testid="hero-strapline">
                <ShieldCheck className="w-4 h-4" /> The future of regulated service compliance
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02] mb-6">
                Compliance support that keeps you{" "}
                <span className="text-brand-gradient">inspection-ready</span>.
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-xl">
                For CQC and Ofsted regulated services. Get AI-powered
                answers, ready-to-use policies, mock interviews, and live regulatory updates — with a
                real specialist whenever the AI can't help.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button asChild size="lg" className="rounded-full px-7 glow-teal hover:-translate-y-0.5 transition-transform" data-testid="hero-get-started">
                  <Link to="/register">Get started <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
                </Button>
                <WatchVideoButton />
                <Button asChild size="lg" variant="ghost" data-testid="hero-try-assistant">
                  <Link to="/assistant"><Sparkles className="w-4 h-4 mr-1.5" /> Try the AI assistant</Link>
                </Button>
                <Button asChild size="lg" variant="ghost" data-testid="hero-take-tour">
                  <Link to="/tour"><MousePointerClick className="w-4 h-4 mr-1.5" /> Take the tour</Link>
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-8" data-testid="hero-stats">
                {[["283", "inspection-ready policies"], ["5", "specialities covered"], ["1", "specialist on standby"], ["24/7", "AI compliance assistant"]].map(([n, l]) => (
                  <span key={l} className="inline-flex items-baseline gap-1.5 rounded-full border border-border bg-card/80 backdrop-blur px-3.5 py-1.5 text-sm shadow-sm">
                    <span className="font-display font-extrabold text-primary">{n}</span><span className="text-muted-foreground text-xs">{l}</span>
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-5 text-sm text-muted-foreground">
                {["Aligned to CQC SAF", "Ofsted SCCIF", "Written by registered managers"].map((t) => (
                  <span key={t} className="flex items-center gap-1.5"><Check className="w-4 h-4 text-primary" /> {t}</span>
                ))}
              </div>
            </motion.div>

            <motion.div initial="hidden" animate="show" variants={fadeUp} custom={2} className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-6 bg-brand-gradient opacity-20 rounded-[2rem] blur-2xl" />
                <div className="relative grid grid-cols-3 gap-3 sm:gap-4" data-testid="hero-collage">
                  {HERO_IMAGES.map((h) => (
                    <Link key={h.id} to={HERO_LINKS[h.id]} className="group relative overflow-hidden rounded-2xl shadow-xl border border-border focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" data-testid={`hero-tile-${h.id}`} aria-label={`${h.tag} — see what's included`}>
                      <img src={h.src} alt={h.alt} className="w-full h-full object-cover aspect-[4/3] transition-transform duration-500 group-hover:scale-105" data-testid={`hero-image-${h.id}`} />
                      <span className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
                      <span className="absolute inset-x-0 bottom-0 px-3 py-2 text-white">
                        <span className="block text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.12em] leading-tight">{h.tag}</span>
                        <span className="hidden sm:flex items-center gap-1 text-[10px] text-white/85 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">See what's included <ArrowRight className="w-3 h-3" /></span>
                      </span>
                    </Link>
                  ))}
                  <Link to="/assistant" className="col-span-3 rounded-2xl bg-brand-gradient text-white shadow-xl p-4 flex items-center gap-4 hover:brightness-110 transition-[filter]" data-testid="hero-ai-tile">
                    <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                      <MessageSquareText className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold leading-tight">AI Compliance Assistant — online now</p>
                      <p className="text-xs text-white/80 mt-0.5">Ask anything about CQC or Ofsted; escalates to a real specialist when needed</p>
                    </div>
                    <ArrowRight className="w-4 h-4 shrink-0" />
                  </Link>
                </div>
                <p className="text-center text-xs text-muted-foreground mt-3" data-testid="hero-collage-hint">Click a specialism to see exactly what's included for your service</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <VideoSlot />
      <FoundersHero />
      <PackShowcase />

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="max-w-2xl mb-12">
          <span className="overline text-accent">Everything in one place</span>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">
            Built for the realities of running a regulated service
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} initial="hidden" whileInView="show" viewport={{ once: true }}
              variants={fadeUp} custom={i}
              className="card-accent-top bg-card border border-border rounded-xl p-6 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-[transform,box-shadow] duration-300">
              <div className="w-11 h-11 rounded-xl bg-brand-gradient text-white flex items-center justify-center mb-4 shadow">
                <f.icon className="w-5 h-5" />
              </div>
              <h3 className="font-display font-semibold text-lg mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Dual tracks */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-2xl mb-10">
          <span className="overline text-accent">Who we support</span>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">
            From older adults to children, clinics and hospitals
          </h2>
          <p className="text-muted-foreground mt-3">
            Adult social care, children and young people, clinical aesthetics, GP practices and independent hospitals — the standards, policies and interview prep are tailored to your service.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12" data-testid="who-we-support">
          {[
            { src: "https://images.unsplash.com/photo-1702648156180-25d8be9c9527?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000", alt: "Carer and older resident sharing a photo album", tag: "CQC · Adult social care", title: "Care homes, home care & supported living", to: "/cqc" },
            { src: "https://images.unsplash.com/photo-1511632765486-a01980e01a18?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000", alt: "Youth worker with a group of young people at sunset", tag: "Ofsted · Children & young people", title: "Children's homes, fostering, supported accommodation & residential settings", to: "/ofsted" },
            { src: "https://images.unsplash.com/photo-1512290923902-8a9f81dc236c?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000", alt: "Clinician giving a cosmetic injectable treatment in a clinic", tag: "CQC · Clinical aesthetics", title: "Aesthetics & cosmetic clinics offering regulated treatments", to: "/aesthetics" },
            { src: "https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000", alt: "Beauty therapist applying a facial treatment", tag: "CQC · Beauty & wellbeing", title: "Beauty, skin and wellbeing practitioners preparing for licensing", to: "/aesthetics" },
            { src: "https://images.unsplash.com/photo-1551190822-a9333d879b1f?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000", alt: "Surgical team in theatre at an independent hospital", tag: "CQC · GP & hospitals", title: "GP practices, independent hospitals and clinics", to: "/gp-hospitals" },
            { src: "https://images.unsplash.com/photo-1609220136736-443140cffec6?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000", alt: "Foster carer with two happy children outdoors", tag: "Ofsted · Fostering agencies", title: "Independent fostering agencies, panels and supervising social workers", to: "/fostering" },
          ].map((c, i) => (
            <motion.div key={c.tag} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}>
              <Link to={c.to} className="block relative overflow-hidden rounded-2xl border border-border group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" data-testid={`support-card-${c.to.replace(/\W/g, "")}`} aria-label={`${c.tag} — see what's included`}>
                <img src={c.src} alt={c.alt} loading="lazy" className="w-full aspect-[4/5] sm:aspect-[16/10] lg:aspect-[3/4] object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                <span className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/75 via-black/30 to-transparent text-white">
                  <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#8fd8de]">{c.tag}</span>
                  <span className="block font-display font-bold text-base leading-snug">{c.title}</span>
                  <span className="inline-flex items-center gap-1 text-xs text-white/85 mt-1.5 opacity-80 group-hover:opacity-100">See what's included <ArrowRight className="w-3 h-3" /></span>
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {TRACKS.map((t, i) => (
            <motion.div key={t.title} initial="hidden" whileInView="show" viewport={{ once: true }}
              variants={fadeUp} custom={i}
              className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-sm flex flex-col hover:border-primary/50 hover:shadow-lg transition-[border-color,box-shadow] duration-300">
              <Link to={t.to} aria-label={t.cta} className="block overflow-hidden"><img src={t.img.src} alt={t.img.alt} loading="lazy" className="w-full h-44 object-cover hover:scale-105 transition-transform duration-500" data-testid={`track-image-${t.img.id}`} /></Link>
              <div className="p-8 flex-1 flex flex-col">
              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-5 -mt-14 relative bg-card border border-border shadow">
                <t.icon className="w-6 h-6 text-accent" />
              </div>
              <span className="overline text-muted-foreground">{t.tag}</span>
              <h3 className="font-display text-2xl font-bold tracking-tight mt-1 mb-4">{t.title}</h3>
              <ul className="space-y-2.5 mb-6 flex-1">
                {t.points.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-sm">
                    <Check className="w-4 h-4 text-accent mt-0.5 shrink-0" /> {p}
                  </li>
                ))}
              </ul>
              <Button asChild variant="outline" className="self-start">
                <Link to={t.to}>{t.cta} <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
              </Button>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Young people's voice */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="young-people-voice">
        <div className="relative overflow-hidden rounded-3xl border border-accent/20 bg-secondary/60 grid lg:grid-cols-2">
          <Link to="/ofsted" aria-label="Ofsted children's homes — see what's included" className="block overflow-hidden" data-testid="young-people-image-link">
            <img src="https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000"
              alt="Young people celebrating together" className="w-full h-64 lg:h-full object-cover hover:scale-[1.03] transition-transform duration-500" />
          </Link>
          <div className="p-8 lg:p-12">
            <span className="overline text-accent">Ofsted · Children's voice</span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-2 mb-3">Evidence that young people shape their care</h2>
            <p className="text-muted-foreground mb-5">
              Quality Standard 7 and the SCCIF ask how children's wishes and feelings drive decisions. Use our participation record to capture
              their words monthly, agree actions, and feed back what changed — ready for Regulation 44 visits and your Regulation 45 review.
            </p>
            <ul className="space-y-2 mb-6 text-sm">
              {["Prompts written for young people, not staff", "Space for agreed actions and feedback dates", "Maps to Quality Standard 7 & SCCIF evidence"].map((t) => (
                <li key={t} className="flex items-start gap-2"><Check className="w-4 h-4 text-accent mt-0.5 shrink-0" /> {t}</li>
              ))}
            </ul>
            <Button asChild data-testid="download-childrens-views-template">
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/templates/childrens-views.pdf`}>Download the free template (PDF) <ArrowRight className="w-4 h-4 ml-1.5" /></a>
            </Button>
          </div>
        </div>
      </section>

      <Testimonials />

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="relative overflow-hidden rounded-3xl bg-brand-gradient p-10 lg:p-16 text-white">
          <img src="/futuregen-logo.png" alt="" aria-hidden="true" className="absolute -right-10 -bottom-16 w-72 lg:w-96 opacity-15 rotate-6 pointer-events-none select-none" />
          <div className="relative max-w-2xl">
            <Building2 className="w-10 h-10 mb-5 opacity-90" />
            <h2 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight mb-4">
              Start with a free account today
            </h2>
            <p className="text-lg text-white/85 mb-8">
              Ask the AI assistant your first compliance question, browse the resource store, and pick a plan when you're ready.
            </p>
            <Button asChild size="lg" className="rounded-full bg-white text-accent hover:bg-white/90 font-bold" data-testid="cta-register">
              <Link to="/register">Create your free account <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
